// =====================================================
// 📁 POSCreditNoteToolbar.tsx
// =====================================================

import { useEffect, useMemo } from 'react';

import { debounce } from 'lodash';

import InputIconByKey from '../../../../Forms/Input/InputIconByKey';

import POSActionButton from '../shared/POSActionButton';

interface Props {
  query: string;

  onSearch: (query: string) => void;

  onClearAll?: () => void;

  onReturnAll?: () => void;

  disabled?: boolean;
}

const POSCreditNoteToolbar: React.FC<Props> = ({
  query,

  onSearch,

  onClearAll,

  onReturnAll,

  disabled = false,
}) => {
  // =====================================================
  // 🔹 Debounce
  // =====================================================
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearch(value);
      }, 300),
    [onSearch]
  );

  // =====================================================
  // 🔹 Effects
  // =====================================================
  useEffect(() => {
    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch]);

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
        flex
        flex-col
        gap-3

        lg:flex-row
        lg:items-center
        lg:justify-between
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Search */}
      {/* ===================================================== */}
      <div className="w-full flex-1">
        <div
          className="
            flex
            items-center
            overflow-hidden

            rounded-xl
            border
            border-stroke

            bg-white

            dark:border-strokedark
            dark:bg-boxdark
          "
        >
          {/* 🔹 Icon */}
          <div
            className="
              bg-gray-1
              flex
              h-12
              w-12
              shrink-0
              items-center
              justify-center

              border-r
              border-stroke

              dark:border-strokedark
              dark:bg-meta-4
            "
          >
            <InputIconByKey iconKey="search" />
          </div>

          {/* 🔹 Input */}
          <input
            type="text"
            value={query}
            disabled={disabled}
            placeholder="Buscar producto de la venta..."
            onChange={(e) => onSearch(e.target.value)}
            className="
              h-12
              w-full

              bg-transparent

              px-4

              text-sm
              text-black

              outline-none

              placeholder:text-body

              disabled:cursor-not-allowed
              disabled:opacity-60

              dark:text-white
            "
          />
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Actions */}
      {/* ===================================================== */}
      <div className="flex items-center gap-2">
        <POSActionButton
          variant="danger"
          disabled={disabled}
          onClick={onClearAll}
        >
          Limpiar selección
        </POSActionButton>

        <POSActionButton
          variant="primary"
          disabled={disabled}
          onClick={onReturnAll}
        >
          Acreditar todos
        </POSActionButton>
      </div>
    </div>
  );
};

export default POSCreditNoteToolbar;
